import { useReadQuery } from "@apollo/client";
import { QueryRef } from "@apollo/client/react/types/types";
import ArrowBack from "@mui/icons-material/ArrowBack";
import {
  Alert,
  Box,
  Button,
  Paper,
  Skeleton,
  Stack,
  useEventCallback,
  useTheme,
  Typography,
} from "@mui/material";
import { GridRowParams, GridSortModel } from "@mui/x-data-grid-premium";
import { mdHeight, xsHeight } from "components/AppBar";
import { TextCellWithTooltip } from "components/DataGridCell";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { TAB_LINKS_HEIGHT } from "components/TabLinks";
import {
  InvoiceAuditLineFragment,
  InvoiceAuditLinesQuery,
  InvoiceAuditLinesQueryVariables,
  InvoiceAuditDetailSummaryQuery,
  InvoiceAuditDetailSummaryQueryVariables,
  WasteInvoiceChargeAuditResult,
  WasteInvoiceLineKind,
} from "generated-graphql/graphql";
import { useLocalOmnisearch } from "hooks/omnisearch-v2";
import { DataGridToolbar } from "hooks/omnisearch-v2/DataGridToolbar";
import { SuspenseDataGrid } from "hooks/omnisearch-v2/SuspenseDataGrid";
import { useGridPersistence } from "hooks/omnisearch-v2/useGridPersistence";
import { usePageForSearch } from "hooks/omnisearch-v2/usePageForSearch";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useTenant } from "hooks/useTenant";
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { formatInvoiceDate, formatServicePeriod, LINE_KIND_LABELS } from "../utils";
import { AuditErrorBoundary } from "./AuditErrorBoundary";
import { auditFacilityId, auditReportSearch } from "./auditFilters";
import { AuditGridSection } from "./AuditGridSection";
import { AuditFact, AuditIssues, AuditResult, AuditSummary } from "./auditUi";
import {
  AUDIT_RESULT_LABELS,
  formatAuditMoney,
  getOverageAmount,
  getUnderageAmount,
} from "./formatAuditValues";
import { InvoiceAuditChargeTypeCell, InvoiceAuditUnitCell } from "./InvoiceAuditCells";
import { InvoiceAuditLineDrawer } from "./InvoiceAuditLineDrawer";
import { useInvoiceAuditDetailQueries } from "./useInvoiceAuditQueries";

type AuditLine = InvoiceAuditLineFragment;
type AuditLineRow = AuditLine & { vendorName: string };
const INITIAL_SORT: GridSortModel = [{ field: "variance", sort: "desc" }];
const FACTS_GRID = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
  gap: 3,
};

export function InvoiceAuditDetail() {
  const theme = useTheme();
  const { wasteInvoiceId } = useParams();
  const { tenantId } = useTenant();
  const [params] = useSearchParams();
  const facilityId = auditFacilityId(params.get("omnisearch") ?? "");
  const base = `/o/${tenantId}/waste/invoices`;
  const reportLink = `${base}/audit?${auditReportSearch(params)}`;
  return (
    <Stack
      spacing={3}
      sx={{
        p: 3,
        bgcolor: "canvas",
        minHeight: {
          xs: `calc(100vh - ${theme.spacing(xsHeight)} - ${TAB_LINKS_HEIGHT}px)`,
          md: `calc(100vh - ${theme.spacing(mdHeight)} - ${TAB_LINKS_HEIGHT}px)`,
        },
      }}
    >
      <Box>
        <Button component={Link} to={reportLink} startIcon={<ArrowBack />}>
          All audit invoices
        </Button>
      </Box>
      {wasteInvoiceId && (
        <InvoiceAuditLines
          key={`${wasteInvoiceId}:${facilityId ?? ""}`}
          invoiceId={wasteInvoiceId}
          tenantId={tenantId}
          facilityId={facilityId}
          reportLink={reportLink}
        />
      )}
    </Stack>
  );
}

const FILTER_KEYS: FilterKey[] = [
  { key: "facilityId", header: "Facilities", filterKeyType: "wasteMultipleFacility" },
];
const FILTER_GROUPS: FilterGroup[] = [
  {
    name: "Invoice line",
    fields: [
      "serviceDate",
      "facilityId",
      "kind",
      "description",
      "quantity",
      "billingUnit",
      "baseAmount",
      "expected",
      "overage",
      "underage",
      "variance",
      "result",
    ],
  },
];
const PAGE_SIZES = [25, 50, 100];
const getItems = (data: InvoiceAuditLinesQuery): AuditLineRow[] =>
  data.wasteInvoiceChargeAuditLines.items.map((audit) => ({
    ...audit,
    vendorName: data.wasteInvoiceChargeAuditLines.vendorName,
  }));
const getCount = (data: InvoiceAuditLinesQuery) => data.wasteInvoiceChargeAuditLines.count;
const getRowId = (row: AuditLine) => row.line.id;

const COLUMNS: OmnisearchGridColDef<AuditLineRow>[] = [
  {
    field: "serviceDate",
    headerName: "Service date",
    filterKeyType: "date",
    width: 125,
    valueGetter: (_, row) => row.line.serviceDate ?? null,
    valueFormatter: (value) => formatInvoiceDate(value),
  },
  {
    field: "vendorName",
    headerName: "Vendor",
    width: 180,
    sortable: false,
    filterable: false,
    valueGetter: (_, row) => row.vendorName,
    renderCell: ({ row }) => <TextCellWithTooltip>{row.vendorName}</TextCellWithTooltip>,
  },
  {
    field: "facility",
    headerName: "Facilities",
    filterable: false,
    minWidth: 240,
    flex: 1,
    valueGetter: (_, row) => row.facilityName ?? "Not provided",
    renderCell: ({ value }) => <TextCellWithTooltip>{value}</TextCellWithTooltip>,
  },
  {
    field: "kind",
    headerName: "Charge type",
    filterKeyType: "enum",
    enumValues: Object.values(WasteInvoiceLineKind),
    enumPresentationFunction: (value) =>
      Object.entries(LINE_KIND_LABELS).find(([key]) => key === value)?.[1] ?? value,
    width: 140,
    valueGetter: (_, row) => LINE_KIND_LABELS[row.line.kind],
    renderCell: ({ row }) => <InvoiceAuditChargeTypeCell kind={row.line.kind} />,
  },
  {
    field: "description",
    headerName: "Description",
    minWidth: 180,
    flex: 1,
    valueGetter: (_, row) => row.line.description,
    renderCell: ({ value }) => <TextCellWithTooltip>{value}</TextCellWithTooltip>,
  },
  {
    field: "quantity",
    headerName: "Quantity",
    type: "number",
    filterKeyType: "number",
    width: 125,
    valueGetter: (_, row) => (row.line.billingQuantity == null ? null : row.line.billingQuantity),
    renderCell: ({ row }) => row.line.billingQuantity ?? "—",
  },
  {
    field: "billingUnit",
    headerName: "Unit",
    width: 90,
    valueGetter: (_, row) => row.line.billingUnit ?? "—",
    renderCell: ({ row }) => <InvoiceAuditUnitCell unit={row.line.billingUnit} />,
  },
  {
    field: "baseAmount",
    headerName: "Billed",
    width: 130,
    type: "number",
    filterKeyType: "number",
    valueGetter: (_, row) => row.line.baseAmount,
    renderCell: ({ row }) => formatAuditMoney("baseAmount", row.line.baseAmount),
  },
  {
    field: "expected",
    headerName: "Expected",
    width: 145,
    type: "number",
    filterKeyType: "number",
    valueGetter: (_, row) => (row.calculation ? row.calculation.expectedBaseAmount : null),
    renderCell: ({ row }) =>
      formatAuditMoney("expectedBaseAmount", row.calculation?.expectedBaseAmount),
  },
  {
    field: "overage",
    headerName: "Overage",
    width: 120,
    type: "number",
    filterKeyType: "number",
    valueGetter: (_, row) => (row.variance == null ? null : getOverageAmount(row.variance)),
    renderCell: ({ row }) => formatAuditMoney("variance", getOverageAmount(row.variance)),
  },
  {
    field: "underage",
    headerName: "Underage",
    width: 120,
    type: "number",
    filterKeyType: "number",
    valueGetter: (_, row) => (row.variance == null ? null : getUnderageAmount(row.variance)),
    renderCell: ({ row }) => formatAuditMoney("variance", getUnderageAmount(row.variance)),
  },
  {
    field: "variance",
    headerName: "Net variance",
    width: 120,
    type: "number",
    filterKeyType: "number",
    valueGetter: (_, row) => (row.variance == null ? null : row.variance),
    renderCell: ({ row }) => formatAuditMoney("variance", row.variance),
  },
  {
    field: "result",
    headerName: "Result",
    filterKeyType: "enum",
    enumValues: Object.values(WasteInvoiceChargeAuditResult),
    enumPresentationFunction: (value) =>
      Object.entries(AUDIT_RESULT_LABELS).find(([key]) => key === value)?.[1] ?? value,
    width: 170,
    valueGetter: (_, row) => AUDIT_RESULT_LABELS[row.result],
    renderCell: ({ row }) => <AuditResult result={row.result} />,
  },
];

const PERSISTENCE_KEY = "waste-invoice-audit-lines";
const COLUMN_FILTER_KEYS: FilterKey[] = [
  ...COLUMNS.filter((column) => column.filterable !== false).map((column) => ({
    ...column,
    key: column.field,
    header: column.headerName ?? column.field,
  })),
  ...FILTER_KEYS,
];
const GRID_SX = {
  "& .audit-selected": {
    bgcolor: "action.selected",
    borderLeft: "3px solid",
    borderLeftColor: "warning.main",
  },
};

function InvoiceAuditHeader({
  queryRef,
  reportLink,
}: {
  queryRef: QueryRef<InvoiceAuditDetailSummaryQuery, InvoiceAuditDetailSummaryQueryVariables>;
  reportLink: string;
}) {
  const { data } = useReadQuery(queryRef);
  const detail = data.wasteInvoiceChargeAuditDetailSummary;
  const { tenantId } = useTenant();
  const base = `/o/${tenantId}/waste/invoices`;
  useBreadcrumb([
    { label: "Waste", to: `/o/${tenantId}/waste` },
    { label: "Invoice Tracking", to: base },
    { label: "Audit", to: reportLink },
    { label: `Invoice ${detail.invoice.invoiceNumber}` },
  ]);
  return (
    <Stack spacing={3}>
      <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
          Invoice {detail.invoice.invoiceNumber}
        </Typography>
        <Box sx={FACTS_GRID}>
          <AuditFact label="Vendor">{detail.vendorName}</AuditFact>
          <AuditFact label="Invoice date">
            {formatInvoiceDate(detail.invoice.invoiceDate)}
          </AuditFact>
          <AuditFact label="Service period">
            {formatServicePeriod(
              detail.invoice.servicePeriodStart,
              detail.invoice.servicePeriodEnd
            )}
          </AuditFact>
          <AuditFact label="Facilities">{detail.facilityCount.toLocaleString()}</AuditFact>
        </Box>
      </Paper>
      <AuditSummary summary={detail.summary} detail />
      {detail.validationIssues.length > 0 && (
        <Alert severity="warning">
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Invoice validation
          </Typography>
          <AuditIssues issues={detail.validationIssues} />
        </Alert>
      )}
    </Stack>
  );
}

export function InvoiceAuditLines({
  invoiceId,
  tenantId,
  facilityId,
  reportLink,
}: {
  invoiceId: string;
  tenantId?: string;
  facilityId?: string;
  reportLink: string;
}) {
  const omnisearchSource = useLocalOmnisearch();
  const persistence = useGridPersistence({
    persistenceKey: PERSISTENCE_KEY,
    initialSortModel: INITIAL_SORT,
    initialPageSize: 25,
    columns: COLUMNS,
  });
  const { page, setPage } = usePageForSearch(
    JSON.stringify([omnisearchSource.omnisearch, persistence.sortModel, persistence.pageSize])
  );
  const paginationModel = useMemo(
    () => ({ page, pageSize: persistence.pageSize }),
    [page, persistence.pageSize]
  );
  const { setPageSize } = persistence;
  const onPaginationModelChange = useCallback(
    (model: { page: number; pageSize: number }) => {
      setPage(model.page);
      setPageSize(model.pageSize);
    },
    [setPage, setPageSize]
  );
  const variables = useMemo(
    (): InvoiceAuditLinesQueryVariables => ({
      id: invoiceId,
      facilityId,
      search: omnisearchSource.omnisearch,
      ...paginationModel,
      sort: persistence.sortModel.map(({ field, sort }) => ({ field, sort })),
    }),
    [invoiceId, facilityId, omnisearchSource.omnisearch, paginationModel, persistence.sortModel]
  );
  const {
    queryRef,
    retry,
    onRetry,
    summaryRef,
    summaryRetry,
    onSummaryRetry,
    isPending,
    summaryPending,
  } = useInvoiceAuditDetailQueries(variables);
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("lineId");
  const [visibleLines, setVisibleLines] = useState<AuditLine[] | null>(null);
  const [inspectedLine, setInspectedLine] = useState<AuditLine | null>(null);
  const selectedLine =
    visibleLines?.find(({ line }) => line.id === selectedId) ??
    (inspectedLine?.line.id === selectedId ? inspectedLine : null);
  const triggerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (selectedId != null) return;
    if (triggerRef.current?.isConnected) triggerRef.current.focus({ preventScroll: true });
    triggerRef.current = null;
  }, [selectedId]);
  const closeDrawer = useEventCallback(() => {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete("lineId");
        return next;
      },
      { replace: true, preventScrollReset: true }
    );
  });
  const selectLine = useEventCallback((audit: AuditLine) => {
    if (document.activeElement instanceof HTMLElement) triggerRef.current = document.activeElement;
    setInspectedLine(audit);
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set("lineId", audit.line.id);
        return next;
      },
      { replace: selectedId != null, preventScrollReset: true }
    );
  });
  const onRowClick = useCallback(
    ({ row }: GridRowParams<AuditLine>) => selectLine(row),
    [selectLine]
  );
  const getRowClassName = useCallback(
    ({ row }: GridRowParams<AuditLine>) => (row.line.id === selectedId ? "audit-selected" : ""),
    [selectedId]
  );
  return (
    <>
      <Box sx={{ opacity: summaryPending ? 0.6 : 1 }}>
        <AuditErrorBoundary key={summaryRetry} resetKey={summaryRef} onRetry={onSummaryRetry}>
          <Suspense
            fallback={
              <>
                <Skeleton variant="rounded" height={90} />
                <AuditSummary />
              </>
            }
          >
            <InvoiceAuditHeader queryRef={summaryRef} reportLink={reportLink} />
          </Suspense>
        </AuditErrorBoundary>
      </Box>
      <AuditGridSection title="Invoice lines">
        <DataGridToolbar
          compact
          omnisearchSource={omnisearchSource}
          persistenceKey={PERSISTENCE_KEY}
          tenantId={tenantId}
          columnFilterKeys={COLUMN_FILTER_KEYS}
          filterGroups={FILTER_GROUPS}
          columns={COLUMNS}
          columnVisibilityModel={persistence.columnVisibilityModel}
          onColumnVisibilityModelChange={persistence.setColumnVisibilityModel}
        />
        <AuditErrorBoundary
          key={retry}
          resetKey={queryRef}
          onRetry={onRetry}
          onClearFilters={() => omnisearchSource.setOmnisearchImmediate("")}
        >
          <SuspenseDataGrid
            columns={COLUMNS}
            getItems={getItems}
            getCount={getCount}
            queryRef={queryRef}
            isPending={isPending}
            getRowId={getRowId}
            onItemsLoaded={setVisibleLines}
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChange}
            sortModel={persistence.sortModel}
            onSortModelChange={persistence.setSortModel}
            columnVisibilityModel={persistence.columnVisibilityModel}
            onColumnVisibilityModelChange={persistence.setColumnVisibilityModel}
            pageSizeOptions={PAGE_SIZES}
            onRowClick={onRowClick}
            getRowClassName={getRowClassName}
            disableRowSelectionOnClick
            noRowsMessage="No invoice lines match these filters."
            sx={GRID_SX}
          />
        </AuditErrorBoundary>
      </AuditGridSection>
      {selectedId != null && (
        <InvoiceAuditLineDrawer
          invoiceId={invoiceId}
          facilityId={facilityId}
          lineId={selectedId}
          audit={selectedLine}
          onClose={closeDrawer}
          outsideResults={
            visibleLines != null && !visibleLines.some(({ line }) => line.id === selectedId)
          }
        />
      )}
    </>
  );
}
